import { useNow } from "../hooks/useNow";
import { breakdown, formatStats, last, recent } from "../lib/latency";
import { useLatency } from "../store/useLatency";

/**
 * Задержка команд в шапке панели быстрых действий.
 *
 * # Почему отдельный компонент
 *
 * Потому что ему нужен собственный тик: окно съезжает само по себе, без новых
 * замеров, и без часов число застыло бы до следующей команды. Правило
 * `hooks/useNow.ts` запрещает держать такой тик в компоненте, тянущем
 * перерисовку половины экрана, — а панель быстрых действий именно такая. Тем же
 * приёмом и по той же причине вынесен `SeriesNav`.
 *
 * # Почему окно, а не всё кольцо
 *
 * Разбор 13.08.2026: оператор увидел здесь «21408 / 49848 мс». Сервер в тот день
 * был ни при чём — журнал команд показывает 41–50 мс от приёма до вердикта на
 * каждой команде, — но число не отпускало: кольцо живёт всю жизнь вкладки, а
 * команд у скальпера единицы за сессию, и два плохих круга становились медианой
 * навсегда.
 *
 * Порогов и цветов здесь НЕТ намеренно — то же решение, что в `LatencyPanel`:
 * выдумать порог до того, как собраны данные, значит красить панель в тревожный
 * цвет неделю подряд. Вместо порога показан ПОСЛЕДНИЙ замер: он и отвечает на
 * вопрос «плохо ли прямо сейчас».
 */

/** Ширина окна головного числа. Пять минут — это окно рынка, на котором торгуют. */
const WINDOW_MS = 5 * 60_000;

export function LatencyBadge() {
  const samples = useLatency((s) => s.samples);
  // Тик нужен ради ПЕРЕРИСОВКИ, а не ради значения: арифметика идёт по
  // performance.now(), потому что на тех же монотонных часах сняты сами замеры,
  // а вычитать отметки с разных часов нельзя (правило M3, см. lib/latency).
  useNow(5000);
  const now = performance.now();

  const latest = last(samples);
  if (latest === null) {
    return (
      <span
        className="num text-xs text-[var(--color-muted)]"
        title="замеров пока нет: отправьте команду"
      >
        — мс
      </span>
    );
  }

  const window = recent(samples, WINDOW_MS, now);
  const stats = breakdown(window).roundTrip;
  const posl = Math.round(latest.roundTripMs);

  // Окно опустело: команд не было давно. Показывать по нему «—» нечестно — замер
  // есть, просто он старый, и возраст здесь важнее квантилей.
  if (stats.n === 0) {
    return (
      <span
        className="num text-xs text-[var(--color-muted)]"
        title="клик → вердикт; за последние пять минут команд не было"
      >
        посл. {posl} мс · {Math.round((now - latest.at) / 60_000)} мин назад
      </span>
    );
  }

  return (
    <span
      className="num text-xs text-[var(--color-muted)]"
      title={
        "клик → вердикт по часам браузера: последний замер, затем p50 / p95 за пять минут. " +
        "Серверную часть пути показывают ноты place_total_ms и clob_order_latency_ms в /healthz трейдера"
      }
    >
      посл. {posl} · {formatStats(stats)} мс
    </span>
  );
}
